import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import FileItem from "@/components/controls/FileUpload/FileItem";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { kebabCase } from "lodash";
import { cn } from "@/utils/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";

interface Props {
  attachments: SavedAttachmentInfo[];
  name: string;
  emptyText?: string;
  // Overrides the field's own row padding, for a caller that already owns the
  // row it sits in (a linked field's edit-action row) — doubled up, the chips
  // sit indented from every neighbouring field's value.
  className?: string;
}

// Read-only file values (linked fields, SCO answers, formula/read-only fields)
// list the same chips the editable field renders, so every file stays openable
// where the value itself can't be changed. Every editing affordance is withheld,
// and so is the discrete download control — the files belong to the field's
// source entity, and a non-previewable type still downloads on open.
//
// The rollout gate lives here rather than at the call site for the same reason
// DetailAttachmentsField carries it: record detail views resolve every field
// through one switch, and a hook cannot be called from a case branch. Behind the
// flag the filenames still render as text, which is what the field showed before
// chips existed — the value is readable either way, only the affordance changes.
const ReadOnlyAttachmentsField: React.FC<Props> = ({
  attachments,
  name,
  emptyText,
  className
}) => {
  const { t } = useTranslation();
  // Field names arrive camelCase from the API; the selectors carry a feature
  // prefix so they stay unique across every field that renders file chips.
  const testIdBase = `read-only-attachment-${kebabCase(name)}`;
  const { enabled: isFileFieldEnabled, loading: isFileFieldFlagLoading } =
    useFeatureFlag(FeatureFlags.CRM_9188_FILE_FIELD_MGMT);

  if (attachments.length === 0) {
    return (
      <Text variant="body" className={cn("pl-1 px-3", className)}>
        {emptyText ?? t("unknown")}
      </Text>
    );
  }

  if (isFileFieldFlagLoading || !isFileFieldEnabled) {
    return (
      <Text variant="body" className={cn("pl-1 px-3", className)}>
        {attachments.map((attachment) => attachment.filename).join(", ")}
      </Text>
    );
  }

  // The design keeps the preview row to a single line: the first file is
  // named, the rest collapse into a counter that opens the full list. Wrapping
  // instead would push the field's neighbours down as files are added.
  const [firstAttachment, ...remainingAttachments] = attachments;

  return (
    <div
      className={cn("flex items-center gap-1 px-3 py-[2px] min-w-0", className)}
      data-test={`${testIdBase}-files`}
    >
      <FileItem
        attachment={firstAttachment}
        size="small"
        isReadOnly
        dataTest={`${testIdBase}-file-${firstAttachment.id}`}
      />
      {remainingAttachments.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={t("filesUpload.showRemainingFiles", {
                count: remainingAttachments.length
              })}
              className="shrink-0 flex items-center rounded-lg border-[0.5px] border-border-badge-chip-default bg-white-6 py-1 px-[6px] cursor-pointer transition-colors duration-200 hover:bg-surface-hover-strong"
              data-test={`${testIdBase}-overflow`}
            >
              <Text as="span" variant="body">{`+${remainingAttachments.length}`}</Text>
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="flex flex-col gap-2 max-h-[400px] overflow-y-auto w-auto"
            align="start"
            sideOffset={4}
            collisionPadding={8}
          >
            {remainingAttachments.map((attachment) => (
              <FileItem
                key={attachment.id}
                attachment={attachment}
                size="small"
                isReadOnly
                dataTest={`${testIdBase}-file-${attachment.id}`}
              />
            ))}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default ReadOnlyAttachmentsField;
